<template>
  <v-row v-if="loading" no-gutters>
    <v-col cols="12" sm="12" class="d-flex">
      <v-skeleton-loader
        type="image"
        width="20%"
        height="40px"
        class="my-1"
      />
      <v-skeleton-loader
        type="image"
        width="20%"
        height="40px"
        class="my-1 mx-1"
      />
    </v-col>
    <v-col cols="12" sm="12">
      <v-skeleton-loader
            v-if="loading"
            class="mx-auto"
            boilerplate
          >
            <table class="v-data-table elevation-1" style="width: 100%;">
              <thead>
                <tr>
                  <th>
                    <v-skeleton-loader
                      type="image"
                      width="100%"
                      height="40px"
                      class="my-1"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <v-skeleton-loader
                      type="image"
                      width="100%"
                      height="80px"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
      </v-skeleton-loader>
    </v-col>
  </v-row>
  <v-row v-else no-gutters>
    <v-col cols="12" sm="12">
      <v-toolbar color="white" height="56px" flat tile>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              color="primaryDark"
              small
              :disabled="!canUpdate"
              @click="onUpdateDefaultOrderConfiguration()"
            >
              <v-icon size="18" class="mr-1">mdi-floppy</v-icon>
              {{ $t("admin.update") }}
            </a-btn>
          </template>
          <span>{{ $t("admin.update") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              v-bind="attrs"
              v-on="on"
              class="ml-2"
              color="primaryDark"
              small
              @click="onCancel()"
              :disabled="!isDirty"
            >
              <v-icon size="18" class="mr-1">mdi-close-thick</v-icon>
              {{ $t("btnCancel") }}
            </a-btn>
          </template>
          <span>{{ $t("btnCancel") }}</span>
        </v-tooltip>
      </v-toolbar>
    </v-col>
    <v-col cols="12" sm="12">
      <a-data-table
        :headers="headers"
        :items="sortData"
        item-key="value"
        :no-data-text="$t('noData')"
        :no-results-text="$t('emptySearchRecords')"
        :disable-sort="true"
        :hide-default-footer="true"
      >
        <template v-slot:item.item="{ item }">
          <span v-if="item.item === 'Abstract'">{{ $t("admin.lblAbstractSettings") }}</span>
          <span v-else-if="item.item === 'Attendance Note'">{{ $t("admin.lblAttendanceNoteSettings") }}</span>
          <span v-else>{{ item.item }}</span>
        </template>

        <template v-slot:item.num="{ item }">
          <v-text-field
            v-model.number="item.num"
            type="number"
            min="0"
            :max="item.item == 'Abstract' ? '20' : '10'"
            :rules="numberRules"
            @input="onFieldChange"
            @blur="validateNumberField(item, 'num')"
            :error="!!item.numError"
            :error-messages="item.numError"
            class="number-field-input"
            hide-details="auto"
          ></v-text-field>
        </template>
      </a-data-table>
    </v-col>
    <message-sheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></message-sheet>
  </v-row>
</template>

<script>

import { cloneDeep } from "lodash";
import { mapActions } from "vuex";
import { createUUID } from "/src/utils/util";
import MessageSheet from "../MessageSheet.vue";
import tenantConfigService from "../../api/tenantConfigService";

export default {
  components: { MessageSheet },
  name: "DefaultLineNum",
  props: {
    setting: {
      type: Object,
      required: true,
    },
    isChangePrevent: {
      type: Boolean,
      default: false,
    },
    isOpenPrevent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableKey: 0,
      sortData: [],
      originalSortData: null,
      currentConfig: null,
      loading: true,
      displayMessage: false,
      messageType: "",
      message: "",
      numberRules: [
        v => v !== null && v !== undefined && v !== '' || this.$t("widget.fieldRequired"),
        v => !isNaN(v) || this.$t("admin.validNumber"),
        v => (v >= 0) || this.$t("admin.validNumberGreaterOrEqualZero"),
        v => (v <= 20) || this.$t("admin.validNumberMax20"),
      ],
    };
  },
  async created() {
    try {
      const configList = await tenantConfigService.getTenantConfigList();
      const defaultLineNumConfig = configList.find(config => config.configname === "defaultLineNum");
      
      if (defaultLineNumConfig && Array.isArray(defaultLineNumConfig.defaultLineNum)) {
        this.currentConfig = { ...defaultLineNumConfig };
        
        this.sortData = defaultLineNumConfig.defaultLineNum.map(item => ({
          ...item,
          itemError: null,
          numError: null,
        }));
      } else {
        this.currentConfig = null;
        this.sortData = [
          {
            item: "Abstract",
            num: 2,
            itemError: null,
            numError: null,
          },
          {
            item: "Attendance Note",
            num: 1,
            itemError: null,
            numError: null,
          }
        ];
      }
      
      this.originalSortData = cloneDeep(this.sortData);
    } catch (error) {
      console.error('Error loading default line num config:', error);
      this.currentConfig = null;
      this.sortData = [
        {
          item: "Abstract",
          num: 1,
          itemError: null,
          numError: null,
        },
        {
          item: "Attendance Note",
          num: 2,
          itemError: null,
          numError: null,
        }
      ];
      this.originalSortData = cloneDeep(this.sortData);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    tenantId() {
      return localStorage.getItem("tenantId");
    },
    headers() {
      let { columns } = this.setting;
      return columns ? columns.map((obj) => {
        return {
          ...obj,
          text: this.$t(`poll.${obj.text}`),
          class: "header header-text",
          width: "33.33%",
        };
      }) : [
        {
          "text": this.$t('poll.lblNumber'),
          "value": "item",
          "class": "header header-text",
          "width": "50%"
        },
        {
          "text": this.$t('poll.lblNumber'),
          "value": "num",
          "class": "header header-text",
          "width": "50%"
        }
      ]
    },
    isDirty() {
      return (
        JSON.stringify(this.originalSortData) !== JSON.stringify(this.sortData)
      );
    },
    canUpdate() {
      console.log('validateNumberField',this.validateAllFields())
      return this.isDirty && this.validateAllFields();
    },
  },
  watch: {
    isDirty(value) {
      this.$emit("update:isChangePrevent", value);
    },
  },
  methods: {
    ...mapActions({
      updateTenantConfig: "tenant/updateTenantConfig",
      createTenantConfig: "tenant/createTenantConfig",
    }),
    onFieldChange() {
      this.$emit("update:isChangePrevent", this.isDirty);
    },
    validateNumberField(item, field) {
      console.log('validateNumberField',item,field)
      const value = item[field];
      console.log('validateNumberField',value)
      
      this.$set(item, `${field}Error`, null);
      
      if (value === null || value === undefined || value === '') {
        this.$set(item, `${field}Error`, this.$t("widget.fieldRequired"));
        return false;
      }
      
      if (isNaN(value)) {
        this.$set(item, `${field}Error`, this.$t("admin.validNumber"));
        return false;
      }
      
      if (value < 0) {
        this.$set(item, `${field}Error`, this.$t("admin.validNumberGreaterOrEqualZero"));
        return false;
      }
      if (item["item"] === "Abstract" && value > 20) {
        this.$set(item, `${field}Error`, this.$t("admin.validNumberMax20"));
        return false;
      }
      if (item["item"] === "Attendance Note" && value > 10) {
        this.$set(item, `${field}Error`, this.$t("admin.validNumberMax10"));
        return false;
      }
      
      return true;
    },
    validateAllFields() {
      let isValid = true;
      
      this.sortData.forEach(item => {
        if (!this.validateNumberField(item, 'num')) {
          isValid = false;
        }
      });
      
      return isValid;
    },
    preparePayload() {
      if (this.currentConfig && Array.isArray(this.currentConfig.defaultLineNum)) {
        return this.currentConfig.defaultLineNum.map((originalItem, index) => {
          const currentItem = this.sortData[index];
          return {
            ...originalItem,
            num: currentItem ? currentItem.num : originalItem.num
          };
        });
      } else {
        return this.sortData.map(item => {
          const { itemError, numError, ...cleanItem } = item;
          return cleanItem;
        });
      }
    },
    async saveOnNavigation() {
      await this.onUpdateDefaultOrderConfiguration();
      if (this.isOpenPrevent) this.$emit("discard");
    },
    async onUpdateDefaultOrderConfiguration() {
      if (!this.validateAllFields()) {
        this.showMessageSheet(
          this.$t("admin.validationError"),
          "error"
        );
        return;
      }
      
      try {
        const payloadData = this.preparePayload();
        
        const ConfigId = createUUID();
        const setting = this.currentConfig?.id ? {
          ...this.currentConfig,
          defaultLineNum: payloadData,
        } : {
          id: ConfigId,
          tenantid: this.tenantId,
          configname: "defaultLineNum",
          defaultLineNum: payloadData,
        };

        await (this.currentConfig?.id
          ? this.updateTenantConfig(setting)
          : this.createTenantConfig(setting));

        this.$eventHub.$emit("onMessageDisplay", {
          message: this.$t("admin.updateTenantConfigMsg"),
          messageType: "success",
        });
        
        if (this.currentConfig) {
          this.currentConfig.defaultLineNum = cloneDeep(payloadData);
        } else {
          this.currentConfig = {
            id: ConfigId,
            tenantid: this.tenantId,
            configname: "defaultLineNum",
            defaultLineNum: cloneDeep(payloadData),
          };
        }
        
        this.originalSortData = cloneDeep(this.sortData);
        this.$emit("update:isOpenPrevent", false);
        this.$emit("update:isChangePrevent", false);
        this.$emit("switchConfig", true);
      } catch (error) {
        console.error("Update configuration failed:", error);
      }
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    onCancel() {
      this.sortData = cloneDeep(this.originalSortData);
      this.tableKey++;
    },
  },
};
</script>

<style scoped>
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.23);
}

::v-deep .v-text-field--outlined.error--text fieldset {
  border-color: #ff5252 !important;
}

::v-deep .v-messages__message {
  font-size: 12px;
  color: #ff5252;
}
::v-deep .v-input__slot:before{
  opacity: 0 !important;
}
</style>